// Query-key factory for the staff record-scoped prequalification-site read
// (stale-after-save convention: queryKey array literals live only in *Keys
// files).
//
// These reads are derived from the partner-brands table, so they are
// invalidated by writes in TWO places: the inheritance-review stamp
// (useConfirmPrequalSiteInheritance) and every partner-brand mutation that
// changes a brand's existence, ownership, or active state — which must go
// through invalidatePartnerBrandAndSiteReads in usePartnerBrands.ts rather
// than invalidating partnerBrandKeys alone. Invalidate by the `all` prefix.

import type { PrequalSiteCompletionListQuery } from "@/api/prequalSiteAdmin";

export const prequalSiteAdminKeys = {
  all: ["prequal-site-admin"] as const,
  broker: (brokerId: string) => [...prequalSiteAdminKeys.all, "broker", brokerId] as const,
  brokerage: (brokerageId: string) =>
    [...prequalSiteAdminKeys.all, "brokerage", brokerageId] as const,
  buyerTeam: (buyerTeamId: string) =>
    [...prequalSiteAdminKeys.all, "buyer-team", buyerTeamId] as const,

  // Paged completions. Nested UNDER the record's own key so the `all` prefix
  // still covers them in one invalidation, and so a record's card and its pages
  // move together — they read the same rows through the same resolved site.
  //
  // The query is part of the key: page, search term and sort each identify a
  // different result set, and sharing one key across them would serve the wrong
  // page out of cache on every filter change.
  brokerCompletions: (brokerId: string, query: PrequalSiteCompletionListQuery) =>
    [...prequalSiteAdminKeys.broker(brokerId), "completions", query] as const,
  brokerageCompletions: (brokerageId: string, query: PrequalSiteCompletionListQuery) =>
    [...prequalSiteAdminKeys.brokerage(brokerageId), "completions", query] as const,
  buyerTeamCompletions: (buyerTeamId: string, query: PrequalSiteCompletionListQuery) =>
    [...prequalSiteAdminKeys.buyerTeam(buyerTeamId), "completions", query] as const,
};
